import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type DataProviderChildren, DataProviderQueryStatus } from '../types';
import { toSubsidiaryQueryStatus } from './company-mapper-queries';
import type { SubsidiaryTreeNode } from './expand-subsidiaries';
import { fetchSubsidiaryTree } from './fetch-subsidiary-tree';
import { usePrimaryCompany } from './use-primary-company';

type SubsidiaryExportRow = {
  Level: number;
  Company: string;
  Parent: string;
  RCID: number;
  Employees: number;
};

export type SubsidiaryTreeData = {
  rcid: number;
  rootName: string;
  tree: SubsidiaryTreeNode;
  exportRows: SubsidiaryExportRow[];
};

const toExportRows = (
  tree: SubsidiaryTreeNode,
  rootName: string
): SubsidiaryExportRow[] => {
  const rows: SubsidiaryExportRow[] = [];
  const visit = (node: SubsidiaryTreeNode, parentName: string) => {
    const name = node.depth === 0 ? rootName : node.name;
    rows.push({
      Level: node.depth,
      Company: name,
      Parent: parentName,
      RCID: node.rcid,
      Employees: node.total,
    });
    for (const child of node.children) visit(child, name);
  };
  visit(tree, '');
  return rows;
};

/**
 * Subsidiaries of the primary company chip, from company-mapper. The tree
 * ignores every other page filter. `data` is null when no company is selected.
 */
export const SubsidiaryTreeDataProvider = ({
  children,
}: {
  children: DataProviderChildren<SubsidiaryTreeData | null>;
}) => {
  const company = usePrimaryCompany();
  const rcid = company?.rcid ?? null;
  const rootName = company?.name ?? '';
  const queryClient = useQueryClient();

  const {
    data: tree,
    status,
    error,
  } = useQuery<SubsidiaryTreeNode>({
    queryKey: ['company-subsidiary-tree', rcid],
    queryFn: () => fetchSubsidiaryTree(rcid as number, queryClient),
    enabled: rcid !== null,
    staleTime: Number.POSITIVE_INFINITY,
    // Every mapper request inside fetchSubsidiaryTree retries once already.
    retry: false,
    meta: { skipGlobalErrorHandling: true },
  });

  const data = useMemo(
    () =>
      rcid === null || tree === undefined
        ? undefined
        : { rcid, rootName, tree, exportRows: toExportRows(tree, rootName) },
    [rcid, rootName, tree]
  );

  if (rcid === null) {
    return children({ data: null, status: DataProviderQueryStatus.Success });
  }
  return children({
    data,
    status: toSubsidiaryQueryStatus(status),
    ...(error !== null && { error }),
  });
};
